import * as PIXI from 'pixi.js';
import { Entity } from '../core/EntityManager';
import { Vector2 } from '../core/Vector2';
import { ENEMY_VISION_RADIUS, ENEMY_VISION_ANGLE, ENEMY_STATS } from '@hamsters/game-config';
import type { EnemyType } from '@hamsters/game-config';
import type { Player } from './Player';
import { PhishingTrap } from './PhishingTrap';
import { getSprite } from '../content/SpriteCache';

export type EnemyState = 'PATROL' | 'SUSPICIOUS' | 'INVESTIGATE' | 'CHASE' | 'RETURN';

const ENEMY_SPRITE_KEYS: Record<EnemyType, string> = {
  BASIC_SCAMMER: 'enemy_basic',
  SUPPORT_SCAMMER: 'enemy_support',
  ARMORED_SCAMMER: 'enemy_armored',
  SPAMMER: 'enemy_spammer',
  INVESTOR_SCAMMER: 'enemy_investor',
};

export class Enemy extends Entity {
  private enemySprite: PIXI.Sprite | null = null;
  private visionGraphic: PIXI.Graphics;
  private state: EnemyState = 'PATROL';
  private patrolPath: Vector2[] = [];
  private patrolIndex = 0;
  private stuckTimer = 0;
  private speed: number;
  private hp: number;
  private visionRadius = ENEMY_VISION_RADIUS;
  private visionAngle = ENEMY_VISION_ANGLE;
  private facing = new Vector2(1, 0);
  private lastKnownPlayerPos: Vector2 | null = null;
  private investigateTimer = 0;
  private enemyType: EnemyType;

  // Support scammer trap ability
  private trapCooldown = 0;
  private readonly trapCooldownMax = 8000;
  public traps: PhishingTrap[] = [];
  private parentContainer: PIXI.Container | null = null;
  private walls: Array<{ x: number; y: number; width: number; height: number }> = [];

  constructor(type: EnemyType, patrolPath: Vector2[] = []) {
    super();
    this.enemyType = type;
    const stats = ENEMY_STATS[type];
    this.speed = stats.speed;
    this.hp = stats.hp;
    this.radius = 16;

    this.patrolPath = patrolPath.map((p) => p.clone());
    if (this.patrolPath.length > 0) {
      const first = this.patrolPath[0];
      if (first) this.position.copy(first);
    }

    // Load sprite from cache
    const spriteKey = ENEMY_SPRITE_KEYS[this.enemyType] ?? 'enemy_basic';
    const texture = getSprite(spriteKey);
    if (texture) {
      this.enemySprite = new PIXI.Sprite(texture);
      this.enemySprite.anchor.set(0.5);
      this.enemySprite.width = this.radius * 2.5;
      this.enemySprite.height = this.radius * 2.5;
      this.sprite.addChild(this.enemySprite);
    } else {
      this.drawFallback();
    }

    // Vision cone
    this.visionGraphic = new PIXI.Graphics();
    this.sprite.addChild(this.visionGraphic);
  }

  private drawFallback(): void {
    const graphics = new PIXI.Graphics();
    graphics.circle(0, 0, this.radius);
    graphics.fill(0xff4444);
    graphics.circle(-4, -3, 2);
    graphics.fill(0xffffff);
    graphics.circle(4, -3, 2);
    graphics.fill(0xffffff);
    graphics.circle(-4, -3, 1);
    graphics.fill(0x000000);
    graphics.circle(4, -3, 1);
    graphics.fill(0x000000);
    this.sprite.addChild(graphics);
  }

  update(dt: number, _tick: number): void {
    switch (this.state) {
      case 'PATROL':
        this.updatePatrol(dt);
        break;
      case 'SUSPICIOUS':
        this.updateSuspicious(dt);
        break;
      case 'INVESTIGATE':
        this.updateInvestigate(dt);
        break;
      case 'CHASE':
        break;
      case 'RETURN':
        this.updateReturn(dt);
        break;
    }

    this.sprite.x = this.position.x;
    this.sprite.y = this.position.y;
    this.drawVision();
  }

  // Move toward target, with smart wall avoidance
  private moveToward(targetX: number, targetY: number, dt: number, speedMul = 1): boolean {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return false;

    const nx = dx / dist;
    const ny = dy / dist;
    const step = this.speed * speedMul * dt;

    const newX = this.position.x + nx * step;
    const newY = this.position.y + ny * step;

    // Try direct movement
    this.position.x = newX;
    this.position.y = newY;

    // Check how many walls we're touching
    let wallCount = 0;
    for (const wall of this.walls) {
      if (this.overlapsWall(wall)) {
        wallCount++;
      }
    }

    if (wallCount >= 2) {
      // CORNER: revert and stop completely this frame
      this.position.x = newX - nx * step;
      this.position.y = newY - ny * step;
      return false;
    }

    if (wallCount === 1) {
      // Single wall - slide along it
      this.position.x = newX - nx * step;
      this.position.y = newY - ny * step;

      const escape = this.findEscapeDirection(targetX, targetY);
      if (escape) {
        this.position.x += escape.x * step * 1.2;
        this.position.y += escape.y * step * 1.2;
        this.facing.set(escape.x, escape.y);
      }

      for (const wall of this.walls) {
        if (this.overlapsWall(wall)) {
          this.pushOutOfWall(wall);
        }
      }
      return false;
    }

    this.facing.set(nx, ny);
    return true;
  }

  // Check if enemy is stuck in a corner (2+ walls blocking)
  private isInCorner(): boolean {
    let count = 0;
    for (const wall of this.walls) {
      if (this.overlapsWall(wall)) count++;
    }
    return count >= 2;
  }

  // Find best direction to escape from walls
  private findEscapeDirection(targetX: number, targetY: number): { x: number; y: number } | null {
    let bestScore = -Infinity;
    let bestDir: { x: number; y: number } | null = null;

    const tdx = targetX - this.position.x;
    const tdy = targetY - this.position.y;
    const tdist = Math.sqrt(tdx * tdx + tdy * tdy);
    const tnx = tdist > 0 ? tdx / tdist : 0;
    const tny = tdist > 0 ? tdy / tdist : 0;

    // Test 8 directions
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const dirX = Math.cos(angle);
      const dirY = Math.sin(angle);
      const testX = this.position.x + dirX * this.radius * 2;
      const testY = this.position.y + dirY * this.radius * 2;

      // Check if this direction is clear
      if (this.wouldCollideAt(testX, testY)) continue;

      // Score: distance from walls + alignment with target direction
      let minWallDist = Infinity;
      for (const wall of this.walls) {
        const wallCX = wall.x + wall.width / 2;
        const wallCY = wall.y + wall.height / 2;
        const d = Math.sqrt((testX - wallCX) ** 2 + (testY - wallCY) ** 2);
        if (d < minWallDist) minWallDist = d;
      }

      const alignment = dirX * tnx + dirY * tny; // -1 to 1
      const score = minWallDist * 0.5 + alignment * 50;

      if (score > bestScore) {
        bestScore = score;
        bestDir = { x: dirX, y: dirY };
      }
    }

    return bestDir;
  }

  private wouldCollideAt(x: number, y: number): boolean {
    for (const wall of this.walls) {
      if (
        x + this.radius > wall.x &&
        x - this.radius < wall.x + wall.width &&
        y + this.radius > wall.y &&
        y - this.radius < wall.y + wall.height
      ) {
        return true;
      }
    }
    return false;
  }

  private overlapsWall(wall: { x: number; y: number; width: number; height: number }): boolean {
    return (
      this.position.x + this.radius > wall.x &&
      this.position.x - this.radius < wall.x + wall.width &&
      this.position.y + this.radius > wall.y &&
      this.position.y - this.radius < wall.y + wall.height
    );
  }

  private pushOutOfWall(wall: { x: number; y: number; width: number; height: number }): void {
    const overlapRight = (this.position.x + this.radius) - wall.x;
    const overlapLeft = (wall.x + wall.width) - (this.position.x - this.radius);
    const overlapBottom = (this.position.y + this.radius) - wall.y;
    const overlapTop = (wall.y + wall.height) - (this.position.y - this.radius);

    const minOverlap = Math.min(overlapRight, overlapLeft, overlapBottom, overlapTop);

    if (minOverlap === overlapRight) {
      this.position.x = wall.x - this.radius - 0.1;
    } else if (minOverlap === overlapLeft) {
      this.position.x = wall.x + wall.width + this.radius + 0.1;
    } else if (minOverlap === overlapBottom) {
      this.position.y = wall.y - this.radius - 0.1;
    } else {
      this.position.y = wall.y + wall.height + this.radius + 0.1;
    }
  }

  private updatePatrol(dt: number): void {
    if (this.patrolPath.length === 0) return;

    this.pushOutOfWalls();

    const target = this.patrolPath[this.patrolIndex];
    if (!target) return;

    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Reached waypoint
    if (dist < 12) {
      this.patrolIndex = (this.patrolIndex + 1) % this.patrolPath.length;
      this.stuckTimer = 0;
      return;
    }

    // Check if in corner - immediate teleport
    if (this.isInCorner()) {
      this.stuckTimer += dt;
      if (this.stuckTimer > 0.15) {
        this.patrolIndex = (this.patrolIndex + 1) % this.patrolPath.length;
        this.stuckTimer = 0;
        const nextTarget = this.patrolPath[this.patrolIndex];
        if (nextTarget) {
          this.position.x = nextTarget.x;
          this.position.y = nextTarget.y;
          this.pushOutOfWalls();
        }
      }
      return;
    }

    const moved = this.moveToward(target.x, target.y, dt);

    if (moved) {
      this.stuckTimer = 0;
    } else {
      this.stuckTimer += dt;
      if (this.stuckTimer > 0.3) {
        this.patrolIndex = (this.patrolIndex + 1) % this.patrolPath.length;
        this.stuckTimer = 0;
        const nextTarget = this.patrolPath[this.patrolIndex];
        if (nextTarget) {
          this.position.x = nextTarget.x;
          this.position.y = nextTarget.y;
          this.pushOutOfWalls();
        }
      }
    }
  }

  private pushOutOfWalls(): void {
    for (const wall of this.walls) {
      if (
        this.position.x + this.radius > wall.x &&
        this.position.x - this.radius < wall.x + wall.width &&
        this.position.y + this.radius > wall.y &&
        this.position.y - this.radius < wall.y + wall.height
      ) {
        const overlapRight = (this.position.x + this.radius) - wall.x;
        const overlapLeft = (wall.x + wall.width) - (this.position.x - this.radius);
        const overlapBottom = (this.position.y + this.radius) - wall.y;
        const overlapTop = (wall.y + wall.height) - (this.position.y - this.radius);

        const minOverlap = Math.min(overlapRight, overlapLeft, overlapBottom, overlapTop);

        if (minOverlap === overlapRight) {
          this.position.x = wall.x - this.radius - 1;
        } else if (minOverlap === overlapLeft) {
          this.position.x = wall.x + wall.width + this.radius + 1;
        } else if (minOverlap === overlapBottom) {
          this.position.y = wall.y - this.radius - 1;
        } else {
          this.position.y = wall.y + wall.height + this.radius + 1;
        }
      }
    }
  }

  private updateSuspicious(dt: number): void {
    this.investigateTimer += dt * 1000;
    if (this.investigateTimer > 1000) {
      this.state = 'INVESTIGATE';
      this.investigateTimer = 0;
    }
  }

  private updateInvestigate(dt: number): void {
    if (!this.lastKnownPlayerPos) {
      this.state = 'RETURN';
      return;
    }

    this.pushOutOfWalls();

    const dx = this.lastKnownPlayerPos.x - this.position.x;
    const dy = this.lastKnownPlayerPos.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 8) {
      // Look around by rotating facing direction
      const rotSpeed = 3;
      const angle = Math.atan2(this.facing.y, this.facing.x);
      this.facing.set(Math.cos(angle + rotSpeed * dt), Math.sin(angle + rotSpeed * dt));

      // Also try to move away from nearby walls to avoid getting stuck
      for (const wall of this.walls) {
        if (this.overlapsWall(wall)) {
          this.pushOutOfWall(wall);
        }
      }

      this.investigateTimer += dt * 1000;
      if (this.investigateTimer > 2000) {
        this.state = 'RETURN';
        this.investigateTimer = 0;
      }
      return;
    }

    this.moveToward(this.lastKnownPlayerPos.x, this.lastKnownPlayerPos.y, dt, 0.8);
  }

  private updateReturn(dt: number): void {
    if (this.patrolPath.length === 0) {
      this.state = 'PATROL';
      return;
    }

    const target = this.patrolPath[this.patrolIndex];
    if (!target) return;

    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 8) {
      this.state = 'PATROL';
      this.stuckTimer = 0;
      return;
    }

    this.pushOutOfWalls();
    const moved = this.moveToward(target.x, target.y, dt);

    if (!moved) {
      this.stuckTimer += dt;
      if (this.stuckTimer > 0.4) {
        // Teleport to target
        this.position.x = target.x;
        this.position.y = target.y;
        this.pushOutOfWalls();
        this.state = 'PATROL';
        this.stuckTimer = 0;
      }
    } else {
      this.stuckTimer = 0;
    }
  }

  canSeePlayer(player: Player, _walls: Array<{ x: number; y: number; width: number; height: number }>): boolean {
    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > this.visionRadius) return false;

    const angle = Math.atan2(dy, dx);
    const facingAngle = Math.atan2(this.facing.y, this.facing.x);
    let angleDiff = angle - facingAngle;
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const halfAngle = (this.visionAngle * Math.PI) / 180 / 2;
    if (Math.abs(angleDiff) > halfAngle) return false;

    return true;
  }

  onPlayerSpotted(playerPos: Vector2): void {
    if (this.state === 'PATROL' || this.state === 'RETURN') {
      this.state = 'SUSPICIOUS';
      this.lastKnownPlayerPos = playerPos.clone();
      this.investigateTimer = 0;
    } else if (this.state === 'INVESTIGATE' || this.state === 'SUSPICIOUS') {
      this.lastKnownPlayerPos = playerPos.clone();
    }
  }

  takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.die();
      return true;
    }
    // Flash red
    if (this.enemySprite) {
      this.enemySprite.tint = 0xff0000;
      setTimeout(() => {
        if (this.enemySprite) this.enemySprite.tint = 0xffffff;
      }, 100);
    }
    return false;
  }

  private die(): void {
    this.active = false;
    const container = this.sprite.parent;
    if (!container) return;

    const colors = [0xff4444, 0xffaa00, 0x44ff44, 0x4444ff, 0xff44ff, 0xffff44];
    const texts = ['!', '?', 'LOL', 'SPAM', 'SCAM', 'X'];

    for (let i = 0; i < 6; i++) {
      const particle = new PIXI.Graphics();
      const color = colors[i % colors.length];
      particle.rect(-8, -6, 16, 12);
      particle.fill({ color, alpha: 0.9 });
      particle.x = this.position.x;
      particle.y = this.position.y;

      const label = new PIXI.Text({
        text: texts[i % texts.length],
        style: { fill: 0xffffff, fontSize: 8, fontFamily: 'Arial', fontWeight: 'bold' },
      });
      label.anchor.set(0.5);
      particle.addChild(label);

      container.addChild(particle);

      const angle = (i / 6) * Math.PI * 2;
      const speed = 80 + Math.random() * 60;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      let life = 0.5;

      const tickerFn = (ticker: PIXI.Ticker) => {
        const dt = ticker.deltaMS / 1000;
        life -= dt;
        particle.x += vx * dt;
        particle.y += vy * dt;
        particle.alpha = life / 0.5;
        particle.rotation += dt * 5;
        if (life <= 0) {
          container.removeChild(particle);
          particle.destroy({ children: true });
          PIXI.Ticker.shared.remove(tickerFn);
        }
      };
      PIXI.Ticker.shared.add(tickerFn);
    }

    this.sprite.visible = false;
  }

  getState(): EnemyState {
    return this.state;
  }

  isAlive(): boolean {
    return this.hp > 0;
  }

  setParentContainer(container: PIXI.Container): void {
    this.parentContainer = container;
  }

  setWalls(walls: Array<{ x: number; y: number; width: number; height: number }>): void {
    this.walls = walls;
  }

  updateTraps(dt: number): void {
    if (this.enemyType !== 'SUPPORT_SCAMMER') return;

    this.trapCooldown += dt * 1000;

    if (this.trapCooldown >= this.trapCooldownMax) {
      this.trapCooldown = 0;
      this.deployTrap();
    }

    this.traps = this.traps.filter((trap) => {
      if (!trap.isActive()) {
        if (trap.sprite.parent) {
          trap.sprite.parent.removeChild(trap.sprite);
        }
        return false;
      }
      return true;
    });
  }

  private deployTrap(): void {
    if (!this.parentContainer) return;

    const trap = new PhishingTrap();
    trap.position.set(this.position.x, this.position.y);
    trap.sprite.x = this.position.x;
    trap.sprite.y = this.position.y;
    this.parentContainer.addChild(trap.sprite);
    this.traps.push(trap);
  }

  getTraps(): PhishingTrap[] {
    return this.traps;
  }

  private drawVision(): void {
    this.visionGraphic.clear();

    const halfAngle = (this.visionAngle * Math.PI) / 180 / 2;
    const facingAngle = Math.atan2(this.facing.y, this.facing.x);

    this.visionGraphic.moveTo(0, 0);
    this.visionGraphic.arc(0, 0, this.visionRadius, facingAngle - halfAngle, facingAngle + halfAngle);
    this.visionGraphic.closePath();
    this.visionGraphic.fill({ color: 0xffff00, alpha: 0.08 });

    let indicatorColor = 0x00ff00;
    if (this.state === 'SUSPICIOUS') indicatorColor = 0xffff00;
    if (this.state === 'INVESTIGATE') indicatorColor = 0xff8800;
    if (this.state === 'CHASE') indicatorColor = 0xff0000;

    this.visionGraphic.circle(0, -20, 4);
    this.visionGraphic.fill(indicatorColor);
  }
}
